import React, { FC, useEffect, useState } from 'react'
import Heading from '../../atoms/Heading/Heading'
import images from '../../../app/utlis/image'
import Slider from 'react-slick'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { getPageData, useData } from '../../../app/helper/functions'
import { ReactComponent as STAR } from '../../../app/assets/images/Star.svg'
import DynamicSVG from '../../atoms/DynamicSvg/DynamicSvg'

interface AppsTestimonialsProps {
    color: string,
    bgcolor: string,
    textColor?: string,
    activeTextColor?: string,
    bgUnActiveColor?: string,
    sliderColor?: string
}

const AppsTestimonials: FC<AppsTestimonialsProps> = ({ color, bgcolor, textColor = 'white', activeTextColor = textColor ?? 'white', bgUnActiveColor = 'bg-blackDarkThemePrimary', sliderColor = '#2e2e2e' }) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const [slidesToShow, setSlidesToShow] = useState(3);

    const { data } = useData();
    const homePageData = getPageData(data, 'HomePageData');
    const testimonialsData = homePageData?.testimonialsData ?? [];

    useEffect(() => {
        const updateSlidesToShow = () => {
            if (window.innerWidth < 768) {
                setSlidesToShow(1);
                return;
            }

            if (window.innerWidth < 1280) {
                setSlidesToShow(2);
                return;
            }

            setSlidesToShow(3);
        };

        updateSlidesToShow();
        window.addEventListener('resize', updateSlidesToShow);

        return () => window.removeEventListener('resize', updateSlidesToShow);
    }, []);

    const activeCardOffset = slidesToShow >= 3 ? 1 : 0;
    const normalizedActiveIndex = testimonialsData.length
        ? ((activeSlide % testimonialsData.length) + testimonialsData.length) % testimonialsData.length
        : 0;
    const highlightedCardIndex = testimonialsData.length
        ? (normalizedActiveIndex + activeCardOffset) % testimonialsData.length
        : -1;

    const settings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 3,
        slidesToScroll: 1,
        swipeToSlide: true,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: false,
        cssEase: "cubic-bezier(0.65, 0, 0.35, 1)",
        beforeChange: (_oldIndex: number, newIndex: number) => setActiveSlide(newIndex),
        responsive: [
            {
                breakpoint: 1280,
                settings: {
                    slidesToShow: 2,
                }
            },
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    autoplaySpeed: 4000,
                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    autoplaySpeed: 5000,
                    centerMode: true,
                    centerPadding: '30px',
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1,
                    autoplaySpeed: 5000,
                    centerMode: true,
                    centerPadding: '15px',
                }
            }
        ],
        appendDots: (dots: React.ReactNode) => (
            <div style={{ textAlign: "center", marginTop: "20px", width: "100%" }} className="mt-8 sm:mt-10 md:mt-12">
                <ul style={{ display: "flex", justifyContent: "center", gap: "4px", padding: "0px", width: "100%", flexWrap: "wrap" }} className="gap-1 sm:gap-2 md:gap-3">
                    {dots}
                </ul>
            </div>
        ),
        customPaging: (i: number) => (
            <motion.div
                initial={{ width: "40px" }}
                whileHover={{
                    scale: 1.2,
                    transition: { type: "spring", stiffness: 400, damping: 17 }
                }}
                style={{
                    width: "40px",
                    height: "5px",
                    backgroundColor: i === normalizedActiveIndex ? `${color}` : `${sliderColor}`,
                    border: `2px solid ${i === normalizedActiveIndex ? color : `${sliderColor}`}`,
                    transition: "background-color 0.5s ease",
                    cursor: 'pointer'
                }}
                className="w-[30px] sm:w-[40px] md:w-[60px] lg:w-[80px]"
            />
        ),
    };

    const cardVariants = {
        enter: (direction: number) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0,
            scale: 0.8,
            rotateY: direction > 0 ? 45 : -45
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1,
            rotateY: 0
        },
        exit: (direction: number) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0,
            scale: 0.8,
            rotateY: direction < 0 ? 45 : -45
        })
    };

    const slideTransition = {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 1,
        duration: 0.8
    };

    const starVariants = {
        hidden: { scale: 0, rotate: -180 },
        visible: (i: number) => ({
            scale: 1,
            rotate: 0,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 15,
                delay: i * 0.1
            }
        })
    };

    return (
        <motion.div
            className='px-4 pt-4 pb-6 md:px-12 md:pt-5 md:pb-8 lg:px-24 lg:pt-6 lg:pb-12'
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                type: "spring",
                stiffness: 200,
                damping: 20,
                duration: 0.8
            }}
        >
            <motion.div
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 20,
                    delay: 0.2
                }}
                className="w-full"
            >
                <Heading className='px-2 sm:px-4 md:px-16 lg:px-32' title='Testimonials' subTitle='Of course we think we are great, but it is not just us who think so' />
            </motion.div>
            <div className='relative overflow-x-hidden w-full'>
                <Slider {...settings} className='mx-1 sm:mx-2 md:mx-4'>
                    {testimonialsData.map((testimonial: any, idx: number) => (
                        <motion.div
                            key={idx}
                            custom={idx < activeSlide ? -1 : 1}
                            variants={cardVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={slideTransition}
                            className="px-2 sm:px-4"
                        >
                            <div className={classNames('relative p-[1px] flex rounded-3xl justify-center')} >
                                <motion.div
                                    className={classNames('p-3 sm:p-4 mt-8 sm:mt-12 flex flex-col gap-4 sm:gap-6 w-full max-w-[500px] relative overflow-visible',
                                        highlightedCardIndex === idx ? `${bgcolor} text-${textColor}` : `${bgUnActiveColor} text-${activeTextColor}`)}
                                    whileHover={{
                                        scale: 1.03,
                                        y: -5,
                                        transition: {
                                            type: "spring",
                                            stiffness: 400,
                                            damping: 17
                                        }
                                    }}
                                    animate={{
                                        scale: highlightedCardIndex === idx ? 1.05 : 1,
                                        y: highlightedCardIndex === idx ? -10 : 0,
                                        transition: {
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 25
                                        }
                                    }}
                                >
                                    <motion.div
                                        className='flex items-start gap-4'
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 25,
                                            delay: 0.2
                                        }}
                                    >
                                        <div className='flex gap-2'>
                                            {Array.from({ length: testimonial.star }).map((_, starIndex) => (
                                                <div key={starIndex}>
                                                    <DynamicSVG color={highlightedCardIndex !== idx ? activeTextColor : textColor} size='w-5 h-5'>{<STAR width={8} height={8} />}</DynamicSVG>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                    <img
                                        src={images.GOOGLE}
                                        alt='Google'
                                        className='absolute z-20 -top-5 right-2 sm:-top-6 sm:right-3 w-12 h-12 sm:w-14 sm:h-14 object-contain pointer-events-none'
                                    />
                                    <motion.div
                                        className='text-sm sm:text-base'
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 25,
                                            delay: 0.3
                                        }}
                                    >
                                        "{testimonial.review}"
                                    </motion.div>
                                    <motion.div
                                        className='flex flex-col sm:flex-row justify-between items-start sm:items-center text-xs sm:text-sm md:text-md gap-1'
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 25,
                                            delay: 0.4
                                        }}
                                    >
                                        <div className='font-bold'>{testimonial.client}</div>
                                        <div>{testimonial.designation}</div>
                                    </motion.div>
                                </motion.div>
                            </div>
                        </motion.div>
                    ))}
                </Slider>
            </div>
        </motion.div>
    )
}

export default AppsTestimonials
